import React, { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/KeycloakContext';
import PhoneInput from 'react-phone-input-2';
import countriesData from '../../data/countriesData.json';
import 'react-phone-input-2/lib/style.css';
import Toast from '../../ui/Toast';
import LeadgenLoading from '../../ui/LeadgenLoading.js';
import './OrgSettings.css';

/**
 * Organization Settings Page
 * - Org Admins/Owners → editable form
 * - Org Members       → read-only company profile card (cannot edit)
 */
function OrgSettings({ embedded }) {
  const { keycloak } = useAuth();
  const {
    workspaceId,
    workspaceName,
    canEditOrgSettings,
    isOrgMember,
    isOrganizationWorkspace,
    isOwner,
  } = useWorkspace();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const [formData, setFormData] = useState({});

  const isSocialMember = !!(
    keycloak?.tokenParsed?.identity_provider ||
    keycloak?.tokenParsed?.idp ||
    keycloak?.tokenParsed?.iss?.includes('google')
  );

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  const resolveMeta = (metadata = {}) => ({
    companyRegistrationNumber:
      metadata?.companyRegistrationNumber || metadata?.company_registration_number || '',
    country: metadata?.country || '',
    phoneNumber: metadata?.phoneNumber || metadata?.phone_number || '',
  });

  const fetchSettings = useCallback(async () => {
    if (!workspaceId || !keycloak?.token) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/settings`, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const normalizedMeta = resolveMeta(result.workspace.metadata || {});
          setSettings(result.workspace);
          setFormData({
            workspace_name: result.workspace.workspace_name || '',
            industry: result.workspace.industry || '',
            companyRegistrationNumber: normalizedMeta.companyRegistrationNumber,
            country: normalizedMeta.country,
            phoneNumber: normalizedMeta.phoneNumber,
            logo_url: result.workspace.logo_url || '',
          });
        }
      } else {
        const err = await response.json();
        setToast({ show: true, message: err.message || 'Failed to fetch settings', type: 'error' });
      }
    } catch {
      setToast({ show: true, message: 'Failed to fetch workspace settings', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, keycloak?.token, API_URL]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // (Toast handles auto-clear)

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setToast({ show: true, message: 'Please upload an image file', type: 'error' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setToast({ show: true, message: 'File size must be less than 5MB', type: 'error' });
      return;
    }

    setLogoLoading(true);
    setToast({ ...toast, show: false });

    const uploadData = new FormData();
    uploadData.append('logo', file);

    try {
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak.token}` },
        body: uploadData,
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setFormData((prev) => ({ ...prev, logo_url: result.logo_url }));
        setToast({ show: true, message: 'Logo updated successfully', type: 'success' });
        // Refresh settings to ensure everything is synced
        fetchSettings();
      } else {
        setToast({ show: true, message: result.message || 'Failed to upload logo', type: 'error' });
      }
    } catch (err) {
      setToast({ show: true, message: 'Failed to upload logo', type: 'error' });
    } finally {
      setLogoLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast({ ...toast, show: false });
    setSaving(true);

    if (!formData.companyRegistrationNumber?.trim()) {
      setToast({ show: true, message: 'Company Registration Number is required', type: 'error' });
      setSaving(false);
      return;
    }
    if (!formData.country?.trim()) {
      setToast({ show: true, message: 'Country is required', type: 'error' });
      setSaving(false);
      return;
    }

    const payload = {
      workspace_name: formData.workspace_name,
      industry: formData.industry,
      metadata: {
        ...(settings?.metadata || {}),
        companyRegistrationNumber: formData.companyRegistrationNumber,
        country: formData.country,
        phoneNumber: formData.phoneNumber,
      },
    };

    try {
      const response = await fetch(`${API_URL}/workspace/${workspaceId}/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${keycloak.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setToast({
          show: true,
          message: 'Workspace settings updated successfully',
          type: 'success',
        });
        setSettings(result.workspace);
      } else {
        setToast({
          show: true,
          message: result.message || 'Failed to update settings',
          type: 'error',
        });
      }
    } catch {
      setToast({ show: true, message: 'Failed to update settings', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="org-settings-container">
        <LeadgenLoading message="Loading workspace settings..." />
      </div>
    );
  }

  // ── Member view: read-only company profile ────────────────────────────────
  if ((isOrgMember || (!canEditOrgSettings && isOrganizationWorkspace)) && !canEditOrgSettings) {
    return (
      <MemberCompanyProfile
        settings={settings}
        workspaceName={workspaceName}
        embedded={embedded}
        isSocialMember={isSocialMember}
      />
    );
  }

  // ── Non-org user (e.g. personal workspace) ────────────────────────────────
  if (!canEditOrgSettings) {
    return (
      <div className="org-settings-container">
        <div className="org-settings-empty">
          <i className="fas fa-lock"></i>
          <h3>Access Restricted</h3>
          <p>Only organization admins can manage workspace settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`org-settings-container ${embedded ? 'embedded' : ''}`}>
      {/* Header */}
      {!embedded && (
        <div className="org-settings-header">
          <h1>
            <i className="fas fa-building"></i>
            Workspace Settings
          </h1>
          <p className="org-settings-subtitle">Manage your workspace settings</p>
        </div>
      )}

      {/* Messages */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {/* Organization Logo Section (Only for Non-OAuth Owners) */}
      {isOwner && !isSocialMember && (
        <div className="form-section">
          <h2 className="form-section-title">
            <i className="fas fa-image"></i>
            Workspace Logo
          </h2>
          <div className="logo-upload-container">
            <div className="logo-preview">
              {formData.logo_url ? (
                <img src={formData.logo_url} alt="Org Logo" />
              ) : (
                <div className="no-logo">
                  <i className="fas fa-building"></i>
                </div>
              )}
            </div>
            <div className="logo-upload-actions">
              <label htmlFor="logo-upload" className="logo-upload-label">
                <i className="fas fa-upload"></i>
                {formData.logo_url ? 'Change Logo' : 'Upload Logo'}
              </label>
              <input
                id="logo-upload"
                type="file"
                hidden
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={logoLoading}
              />
              <p className="logo-upload-hint">Recommended: Square image, max 5MB</p>
              {logoLoading && (
                <div className="logo-loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i> Uploading...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <form onSubmit={handleSubmit} className="org-settings-form">
        {/* Company Info Section */}
        <div className="form-section">
          <h2 className="form-section-title">
            <i className="fas fa-info-circle"></i>
            Company Information
          </h2>

          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="workspace_name">Workspace Name</label>
              <input
                type="text"
                id="workspace_name"
                name="workspace_name"
                value={formData.workspace_name}
                onChange={handleChange}
                placeholder="Enter workspace name"
              />
            </div>

            <div className="form-field">
              <label htmlFor="companyRegistrationNumber">
                Company Registration Number <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                id="companyRegistrationNumber"
                name="companyRegistrationNumber"
                value={formData.companyRegistrationNumber || ''}
                onChange={handleChange}
                placeholder="Enter registration number"
              />
            </div>

            <div className="form-field">
              <label htmlFor="country">
                Country <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                id="country"
                name="country"
                value={formData.country || ''}
                onChange={handleChange}
              >
                <option value="">Select country...</option>
                {countriesData.map((c) => (
                  <option key={c.code} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="phoneNumber">Phone Number</label>
              <PhoneInput
                country={
                  countriesData.find((c) => c.name === formData.country)?.code?.toLowerCase() ||
                  'my'
                }
                value={formData.phoneNumber || ''}
                onChange={(phone) => {
                  setFormData((prev) => ({
                    ...prev,
                    phoneNumber: phone,
                  }));
                }}
                disableDropdown={true}
                placeholder="Enter phone number"
                inputProps={{
                  name: 'phoneNumber',
                  id: 'phoneNumber',
                }}
              />
            </div>
          </div>
        </div>

        {/* Workspace Info (Read-only) */}
        <div className="form-section">
          <h2 className="form-section-title">
            <i className="fas fa-cog"></i>
            Workspace Info
          </h2>

          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Workspace Type</span>
              <span className="info-value">
                <span className="info-badge org">{settings?.workspace_type || '—'}</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Created</span>
              <span className="info-value">
                {settings?.created_at
                  ? new Date(settings.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : '—'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Last Updated</span>
              <span className="info-value">
                {settings?.updated_at
                  ? new Date(settings.updated_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="form-actions">
          <button type="submit" className="save-btn" disabled={saving}>
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MemberCompanyProfile — read-only company profile for org MEMBERS
// ─────────────────────────────────────────────────────────────────────────────
function MemberCompanyProfile({ settings, workspaceName, embedded, isSocialMember }) {
  const fmt = (v) => v || <span style={{ color: '#94a3b8' }}>—</span>;
  const fmtDate = (d) =>
    d ? (
      new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    ) : (
      <span style={{ color: '#94a3b8' }}>—</span>
    );
  const metadata = settings?.metadata || {};
  const companyRegistrationNumber =
    metadata?.companyRegistrationNumber || metadata?.company_registration_number;
  const phoneNumber = metadata?.phoneNumber || metadata?.phone_number;

  return (
    <div className={`org-settings-container ${embedded ? 'embedded' : ''}`}>
      {!embedded && (
        <div
          className="org-settings-header"
          style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}
        >
          <div style={{ flex: 1 }}>
            <h1>
              <i className="fas fa-building"></i> Workspace Profile
            </h1>
            <p className="org-settings-subtitle">
              Your workspace's details — managed by the admin.
            </p>
          </div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              color: '#64748b',
              borderRadius: '20px',
              padding: '4px 12px',
              fontSize: '11.5px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
            }}
          >
            <i className="fas fa-eye"></i> View Only
          </span>
        </div>
      )}

      {/* Info fields */}
      <div className="form-section">
        <h2 className="form-section-title">
          <i className="fas fa-info-circle"></i> Company Information
        </h2>

        {settings?.logo_url && !isSocialMember && (
          <div
            className="logo-upload-container"
            style={{ borderStyle: 'solid', background: '#fff', marginBottom: '24px' }}
          >
            <div className="logo-preview">
              <img src={settings.logo_url} alt="Org Logo" />
            </div>
            <div className="logo-upload-actions">
              <span className="info-label">Workspace Logo</span>
              <p className="org-settings-subtitle" style={{ margin: 0 }}>
                This branding appears on your outreach emails.
              </p>
            </div>
          </div>
        )}

        <div className="info-grid">
          <ProfileField label="Workspace Name" value={fmt(settings?.workspace_name)} />
          <ProfileField label="Industry" value={fmt(settings?.industry)} />
          <ProfileField label="Comp. Reg. Number" value={fmt(companyRegistrationNumber)} />
          <ProfileField label="Country" value={fmt(metadata?.country)} />
          <ProfileField label="Phone Number" value={fmt(phoneNumber)} />
          <ProfileField
            label="Workspace Type"
            value={<span className="info-badge org">{settings?.workspace_type || '—'}</span>}
          />
          <ProfileField label="Created" value={fmtDate(settings?.created_at)} />
          <ProfileField label="Last Updated" value={fmtDate(settings?.updated_at)} />
        </div>
      </div>
    </div>
  );
}

function ProfileField({ label, value }) {
  return (
    <div className="info-item">
      <span className="info-label">{label}</span>
      <span className="info-value">{value}</span>
    </div>
  );
}

export default OrgSettings;
